import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: "", email: "", phone: "", company: "", message: "" };

export const useCalendarStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connected, setConnected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/calendar/status`).then((r) => setConnected(r.data.connected)).catch(() => setConnected(false));
  }, []);

  useEffect(() => {
    if (searchParams.get("calendar") === "connected") {
      toast.success("Agenda Google connecté avec succès");
      setConnected(true);
      searchParams.delete("calendar");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return connected;
};

export const useSlots = (date) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    axios
      .get(`${API}/availability`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [date]);

  return { slots, loading };
};

export const useOffer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const offer = searchParams.get("offre") || "";
  const clear = () => {
    searchParams.delete("offre");
    setSearchParams(searchParams, { replace: true });
  };
  return { offer, clear };
};

const validate = (form, clientType) => {
  if (clientType === "professionnel" && !form.company) return "Merci d'indiquer le nom de votre entreprise";
  if (!form.name || !form.email || !form.phone) return "Merci de remplir votre nom, email et téléphone";
  return null;
};

export const useBookingForm = (offer) => {
  const [step, setStep] = useState(0);
  const [clientType, setClientType] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [date, setDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const selectDate = (d) => { setDate(d); setSelectedSlot(null); };
  const selectClientType = (v) => { setClientType(v); setNeeds([]); };
  const toggleNeed = (n) => setNeeds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  const canNext = [Boolean(clientType), needs.length > 0, Boolean(selectedSlot), true][step];
  const back = () => setStep((s) => Math.max(0, s - 1));
  const next = () => canNext && setStep((s) => s + 1);

  const submit = async () => {
    const error = validate(form, clientType);
    if (error) return toast.error(error);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bookings`, { ...form, client_type: clientType, needs, offer, slot_start: selectedSlot });
      setConfirmed(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue, veuillez réessayer");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    step, clientType, needs, date, selectedSlot, form, submitting, confirmed, canNext,
    selectDate, selectClientType, toggleNeed, setSelectedSlot, setForm, back, next, submit,
  };
};
